import React from 'react';
import { APIKey } from '../../types/apiKeys';

interface RegenerateAPIKeyModalProps {
  apiKey: APIKey;
  onClose: () => void;
  onSubmit: (apiKey: APIKey) => void;
}

export const RegenerateAPIKeyModal: React.FC<RegenerateAPIKeyModalProps> = ({ 
  apiKey, 
  onClose, 
  onSubmit 
}) => {
  const handleConfirm = () => {
    onSubmit(apiKey);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Regenerate API Key</h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Warning
                </h3>
                <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                  <p>
                    Regenerating this API key will invalidate the current key immediately. 
                    Any applications using the current key will stop working.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-200">API Key Name:</span>
              <div className="text-gray-900 dark:text-white">{apiKey.name}</div>
            </div>
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-200">Current Key:</span>
              <div className="font-mono text-sm bg-gray-100 dark:bg-gray-800 p-2 rounded mt-1">
                {apiKey.display_key}
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-200">Key Type:</span>
              <div className="text-gray-900 dark:text-white">
                {apiKey.key_type === 'read' ? 'Read Only' : 
                 apiKey.key_type === 'write' ? 'Read & Write' : 'Full Access'}
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-200">Rate Limit:</span>
              <div className="text-gray-900 dark:text-white">{apiKey.rate_limit} requests/hour</div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
          >
            Regenerate Key
          </button>
        </div>
      </div>
    </div>
  );
};
